import { useState } from 'react';
import { categories, practices } from '../lib/catalog';
import { formatLength, practiceSeconds } from '../lib/timeline';
import { beginRun } from '../lib/sessionStore';
import type { Practice } from '../types';
import { Icon } from '../ui/Icon';
import { SunGlow } from '../ui/SunGlow';
import { Sheet } from '../ui/parts';
import { StartPanel } from './StartPanel';
import styles from './screens.module.css';

const PreviewSheet = ({ practice, onClose, onPlay }: { practice: Practice; onClose: () => void; onPlay: (id: string) => void }) => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <Sheet title={practice.title} onClose={onClose}>
      <p className="eyebrow-soft" style={{ marginBottom: 16 }}>
        {practice.blurb}
      </p>

      {practice.external ? (
        <div className={styles.externalActions}>
          <a className="pill-button" href={practice.external.link} target="_blank" rel="noreferrer">
            Open YouTube Music
            <Icon name="external" size={18} />
          </a>
          <p className="eyebrow-soft">{practice.external.note}</p>
          {practice.external.video &&
            (showVideo ? (
              <video className={styles.video} src={practice.external.video} poster="/videos/rishi_ji_aerobics-poster.jpg" controls preload="none" playsInline />
            ) : (
              <button className="text-button" onClick={() => setShowVideo(true)}>
                Watch the video here
              </button>
            ))}
        </div>
      ) : (
        <StartPanel
          compact
          practice={practice}
          onBegin={() => {
            beginRun([practice]);
            onPlay(practice.id);
          }}
        />
      )}
    </Sheet>
  );
};

export const Explore = ({ onPlay }: { onPlay: (id: string) => void }) => {
  const [open, setOpen] = useState<Practice | null>(null);

  return (
    <>
      <SunGlow position="page" />
      <main className={`screen ${styles.explore}`}>
        <h1 className={styles.pageTitle}>Explore</h1>

        {categories.map(cat => {
          const items = practices.filter(p => p.category === cat.id);
          if (!items.length) return null;
          return (
            <section key={cat.id} aria-labelledby={`cat-${cat.id}`}>
              <h2 id={`cat-${cat.id}`} className={styles.sectionTitle}>
                {cat.label}
              </h2>
              <ul className={`${styles.rows} surface`}>
                {items.map(p => (
                  <li key={p.id}>
                    <button className={styles.row} onClick={() => setOpen(p)}>
                      <Icon name={p.icon} size={26} className={styles.rowIcon} />
                      <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{p.title}</span>
                        <span className={styles.rowSub}>{p.note ?? p.blurb}</span>
                      </span>
                      <span className={styles.rowMeta}>{p.external ? 'Opens YouTube Music' : formatLength(practiceSeconds(p))}</span>
                      <Icon name="chevron" size={18} className={styles.rowChevron} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </main>

      {open && <PreviewSheet practice={open} onClose={() => setOpen(null)} onPlay={onPlay} />}
    </>
  );
};
